import { useEffect, useRef, useState } from "react";

import { logApiError } from "../../../utils/logApiError";
import {
  classifyFirmwareOutcome,
  FirmwareOutcome,
} from "./firmwareStatus";
import { FirmwareType, UpdateLog } from "./types";

/**
 * `ОБН.2` (`АРХ-Н22`): запуск обновления и опрос его исхода.
 *
 * Прежде ошибку запуска ловил сам `adminStore.updateFirmWare`, а вкладка её не
 * видела: `catch` не срабатывал никогда, и на 403/404/502 вкладка ставила
 * «обновляется» и запускала опрос. Предела опроса тоже не было.
 *
 * Теперь:
 * - ошибка запуска доходит до вкладки и показывается текстом ответа сервера;
 * - опрос останавливается через **15 мин** от запуска, даже если изделие так и
 *   не ответило: в журнал пишется итог, ожидание снимается;
 * - опрос останавливается при закрытии вкладки — `clearInterval` в уборке.
 */
export const FIRMWARE_POLL_TIMEOUT_MS = 15 * 60 * 1000;
export const FIRMWARE_POLL_INTERVAL_MS = 3_000;
export const FIRMWARE_NO_ANSWER_MESSAGE = "нет ответа изделия за 15 мин";

/** `ОБН.2.1.1`: так шлюз называет изделие, до которого не достал. */
const GATEWAY_NOT_CONNECTED = /device\s+(?:is\s+)?not\s+connected|not connected/i;

type FirmwareUpdateStore = {
  updatingType: FirmwareType | null;
  updatingModuleId: string | null;
  lastUpdateStartedAt: string | null;
  updateLogs: UpdateLog[];
  setUpdatingState: (
    type: FirmwareType,
    moduleId: string,
    startedAt: string,
  ) => void;
  clearUpdatingState: () => void;
  setUpdateStatus: (type: FirmwareType, status: "complete" | "failed") => void;
  updateFirmWare: (
    firmId: number,
    moduleId: string,
    type: FirmwareType,
  ) => Promise<unknown>;
  pollFirmLogs: (moduleId: string, since: string) => Promise<unknown>;
};

/**
 * Текст отказа запуска для человека: сначала то, что сказал сервер, затем
 * понятный код. `404` у шлюза — «изделие не на связи» (`Device not connected`),
 * `502` — шлюз не ответил; у прочих ответов называем код и текст.
 */
export function firmwareStartErrorMessage(error: any): string {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const serverText = data?.message || data?.error || null;

  // `ОБН.2.1.1` (`АУД-Н307`): «изделие не на связи» — только причина шлюза.
  // Прочие 404 back («модуль не найден», «Прошивка не найдена…») показываются
  // своей причиной как есть: иначе неверный образ выглядел бы как потеря связи.
  if (status === 404) {
    if (serverText && !GATEWAY_NOT_CONNECTED.test(serverText)) {
      return serverText;
    }

    return "изделие не на связи";
  }

  if (status === 403) {
    return serverText || "запуск обновления запрещён";
  }

  if (status === 502) {
    return serverText || "шлюз не ответил";
  }

  if (status) {
    return `${status}: ${serverText || error?.message || "ошибка запуска"}`;
  }

  return serverText || error?.message || "Ошибка при запуске обновления";
}

export function useFirmwareUpdate(adminStore: FirmwareUpdateStore) {
  const [updateLogs, setUpdateLogs] = useState<UpdateLog[]>([]);
  const [outcomes, setOutcomes] = useState<FirmwareOutcome[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  // Остановлен ли опрос: ответ на запрос, начатый до остановки, не должен
  // вернуть журнал к прежнему состоянию (и не должен отменить строку о пределе).
  const stoppedRef = useRef(false);

  const clearTimer = () => {
    stoppedRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => clearTimer, []);

  const startPolling = (moduleId: string, since: string) => {
    clearTimer();
    stoppedRef.current = false;
    startedAtRef.current = Date.now();

    intervalRef.current = setInterval(async () => {
      // `ОБН.2`: предел опроса. Число — одной константой, чтобы «15 мин» не
      // разошлось между проверкой, текстом и документацией.
      if (Date.now() - startedAtRef.current >= FIRMWARE_POLL_TIMEOUT_MS) {
        clearTimer();
        stoppedRef.current = true;
        setUpdateLogs((prev) => [
          ...prev,
          { level: "warning", message: FIRMWARE_NO_ANSWER_MESSAGE },
        ]);
        if (adminStore.updatingType) {
          adminStore.setUpdateStatus(adminStore.updatingType, "failed");
        }
        adminStore.clearUpdatingState();
        return;
      }

      try {
        const logs = (await adminStore.pollFirmLogs(
          moduleId,
          since,
        )) as UpdateLog[];

        if (stoppedRef.current) return;
        setUpdateLogs(logs);

        const classified = logs
          .map((log) => classifyFirmwareOutcome(log))
          .filter((outcome): outcome is FirmwareOutcome => outcome !== null);

        if (classified.length > 0) {
          setOutcomes(classified);
        }

        const failures = classified.filter(
          (outcome) => outcome.outcome === "failure",
        );
        const finalOutcome = classified.find((outcome) => outcome.final);

        if (finalOutcome) {
          clearTimer();
          stoppedRef.current = true;

          if (adminStore.updatingType) {
            adminStore.setUpdateStatus(
              adminStore.updatingType,
              failures.length > 0 ? "failed" : "complete",
            );
          }

          adminStore.clearUpdatingState();
        }
      } catch (err) {
        logApiError("Ошибка при получении логов", err);
      }
    }, FIRMWARE_POLL_INTERVAL_MS);
  };

  /**
   * Запуск обновления. Возвращает текст отказа для вкладки или `null`, если
   * запуск принят: ошибку больше не глотает хранилище.
   */
  const startUpdate = async (
    firmId: number,
    moduleId: string,
    type: FirmwareType,
  ): Promise<string | null> => {
    adminStore.setUpdatingState(type, moduleId, new Date().toISOString());
    clearTimer();

    try {
      await adminStore.updateFirmWare(firmId, moduleId, type);
      setUpdateLogs([]);
      startPolling(moduleId, adminStore.lastUpdateStartedAt || new Date().toISOString());
      return null;
    } catch (error: any) {
      adminStore.clearUpdatingState();
      return firmwareStartErrorMessage(error);
    }
  };

  /** Продолжить опрос после переоткрытия вкладки — состояние лежит в хранилище. */
  const resumePolling = () => {
    if (
      adminStore.updatingType &&
      adminStore.updatingModuleId &&
      adminStore.lastUpdateStartedAt
    ) {
      setUpdateLogs(adminStore.updateLogs || []);
      startPolling(adminStore.updatingModuleId, adminStore.lastUpdateStartedAt);
    }
  };

  const stopPolling = () => {
    clearTimer();
    adminStore.clearUpdatingState();
  };

  return {
    updateLogs,
    outcomes,
    startUpdate,
    resumePolling,
    stopPolling,
  };
}
